import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { IShadowState, IUnit } from '../interfaces/interfaces';
import { MqttMessageStorageService } from './mqtt-message-storage.service';
import { DeviceRegistryService } from 'src/app/services/device-registry.service';
import { FRONT_DOOR_LOCK, FRONT_DOOR_LOCK_JAMMED } from '../actions-list';

// Cache will be considered stale after this many milliseconds (3 days)
const CACHE_EXPIRATION_MS = 3 * 24 * 60 * 60 * 1000;

interface CacheData {
  timestamp: number;
  deviceStates: { [key: string]: IShadowState };
}

@Injectable({
  providedIn: 'root'
})
export class DeviceCacheService {
  private cachedDeviceStates: { [key: string]: IShadowState } = {};
  private cacheTimestamp: number = 0;
  private isCacheLoaded$ = new BehaviorSubject<boolean>(false);
  private hasInitializedCache = false;

  constructor(
    private messageStorage: MqttMessageStorageService,
    private deviceRegistry: DeviceRegistryService
  ) {
    // Try to load cache from local storage on service initialization
    this.loadCachedDevices();
    
    // Initialize cached states immediately if available
    // This ensures widgets have data as soon as they're created
    if (Object.keys(this.cachedDeviceStates).length > 0 && !this.isCacheStale()) {
      this.initializeCachedStates();
      
      // Register climate devices with the device registry
      this.registerCachedDevices();
    }
  }

  /**
   * Store device states in cache for faster loading
   */
  public cacheDeviceStates(units?: IUnit[]): void {
    if (!units?.length) return;

    let cachedCount = 0;
    units.forEach(unit => {
      unit.shadowStates?.forEach(state => {
        if (state.shadow_state?.reported) {
          const entityId = state.shadow_state.reported?.entity_id ?? state.shadow_name.replace('-', '.');
          
          // Only cache homepage-relevant entities
          if (this.shouldCacheEntity(entityId)) {
            this.cachedDeviceStates[entityId] = {
              ...state.shadow_state,
              reported: {
                ...state.shadow_state.reported,
                entity_id: entityId
              }
            };
            cachedCount++;
          }
        }
      });
    });

    // Update timestamp and save to localStorage
    this.cacheTimestamp = Date.now();
    this.saveCachedDevices();
    this.isCacheLoaded$.next(true);
  }

  /**
   * Check if this entity should be cached (only homepage-relevant entities)
   */
  private shouldCacheEntity(entityId: string): boolean {
    // Cache room-level lights (ending with _lights)
    if (entityId.startsWith('light.') && entityId.endsWith('_lights')) {
      return true;
    }
    
    // CRITICAL: Cache all_lights entity - this is what triggers device discovery!
    if (entityId === 'light.all_lights') {
      return true;
    }
    
    // Cache specific climate entities for rooms
    if (entityId.startsWith('climate.') && 
        (entityId.includes('livingspace_') || 
         entityId.includes('bedroom_') || 
         entityId.includes('bathroom_'))) {
      return true;
    }
    
    // Cache input_text entities used for device names
    if (entityId.startsWith('input_text.') && entityId.endsWith('_lights')) {
      return true;
    }
    
    // Cache custom light color entities
    if (entityId.startsWith('input_text.custom_light_color_')) {
      return true;
    }
    
    // Cache brightness values for light controls
    if (entityId.startsWith('input_number.') && entityId.endsWith('_lights_brightness')) {
      return true;
    }
    
    // Cache circadian lighting settings
    if (entityId.startsWith('input_boolean.') && entityId.includes('_circadian')) {
      return true;
    }
    
    // Cache light temperature settings
    if (entityId.startsWith('input_number.') && entityId.endsWith('_lights_temperature')) {
      return true;
    }
    
    // Cache alarm panel state
    if (entityId.startsWith('alarm_control_panel.')) {
      return true;
    }

    // Cache the front door lock and its jam sensor, for the Lock button beside
    // the alarm slider. Refreshed on connect by the same entries in
    // requestLatestShadowStates.
    if (entityId === FRONT_DOOR_LOCK || entityId === FRONT_DOOR_LOCK_JAMMED) {
      return true;
    }

    // Cache mood audio, so the transport bar above the scenes carousel comes back
    // with the lights rather than three seconds later.
    //
    // Everything else on the homepage is restored from this cache on the first
    // frame; anything absent from it waits for a live shadow round trip. The bar
    // was in that second group, so on every open it rendered with no amplifier
    // and no sounds — i.e. unavailable — and only corrected once the unit
    // answered. Nothing was wrong with the bar; it was reading an empty map
    // because its entities were never cached.
    //
    // Three entities, matching consts/mood-audio.ts: the state sensor the bar
    // renders from, the folder sensor carrying the sound library, and the
    // amplifier itself, which is what `ampPresent` is judged on.
    if (entityId === 'sensor.mood_audio_state'
        || entityId === 'sensor.mood_audio'
        || entityId === 'media_player.livingspace') {
      return true;
    }

    return false;
  }

  /**
   * Update a specific device state in the cache
   * @param entityId The ID of the entity to update
   * @param state The new state to store
   */
  public updateCachedState(entityId: string, state: IShadowState): void {
    if (!entityId || !this.shouldCacheEntity(entityId)) return;
    
    this.cachedDeviceStates[entityId] = state;
    
    // Update timestamp and save to localStorage
    this.cacheTimestamp = Date.now();
    this.saveCachedDevices();
  }

  /**
   * Initialize message storage with cached states for quick loading
   */
  public initializeCachedStates(): boolean {
    // Skip if already initialized
    if (this.hasInitializedCache) {
      return true;
    }
    
    // Check if we have cache data and it's not stale
    if (Object.keys(this.cachedDeviceStates).length === 0 || this.isCacheStale()) {
      return false;
    }
    
    // Set cached values in the message storage service
    // Only set values that are still relevant for homepage
    Object.entries(this.cachedDeviceStates).forEach(([entityId, state]) => {
      if (this.shouldCacheEntity(entityId)) {
        this.messageStorage.setValue(entityId, { ...state, fromDeviceCache: true });
      }
    });
    
    this.hasInitializedCache = true;
    return true;
  }
  
  /**
   * Check if the cache is stale (older than expiration time)
   */
  private isCacheStale(): boolean {
    const cacheAge = Date.now() - this.cacheTimestamp;
    return cacheAge > CACHE_EXPIRATION_MS;
  }

  /**
   * Save cached devices to localStorage
   */
  private saveCachedDevices(): void {
    try {
      const cacheData: CacheData = {
        timestamp: this.cacheTimestamp,
        deviceStates: this.cachedDeviceStates
      };
      localStorage.setItem('device_cache', JSON.stringify(cacheData));
    } catch (error) {
      // Silent fail on storage errors
    }
  }

  /**
   * Load cached devices from localStorage
   */
  private loadCachedDevices(): void {
    try {
      const cachedDataString = localStorage.getItem('device_cache');
      if (cachedDataString) {
        const cachedData: CacheData = JSON.parse(cachedDataString);
        
        // Load both the states and timestamp
        this.cachedDeviceStates = cachedData.deviceStates || {};
        this.cacheTimestamp = cachedData.timestamp || 0;
        
        const deviceCount = Object.keys(this.cachedDeviceStates).length;
        
        if (!this.isCacheStale() && deviceCount > 0) {
          this.isCacheLoaded$.next(true);
        }
      }
    } catch (error) {
      console.error('[DeviceCache] Error loading cache:', error);
      // If there was an error, clear the cache to avoid further issues
      this.clearCache();
    }
  }

  /**
   * Get observable for cache load status
   */
  public isCacheLoaded(): Observable<boolean> {
    return this.isCacheLoaded$.asObservable();
  }

  /**
   * Clear the device cache
   */
  public clearCache(): void {
    this.cachedDeviceStates = {};
    this.cacheTimestamp = 0;
    localStorage.removeItem('device_cache');
    this.isCacheLoaded$.next(false);
    this.hasInitializedCache = false;
  }
  
  /**
   * Force refresh cache - for debugging
   * Can be called from console: window.clearDeviceCache()
   */
  public forceRefreshCache(): void {
    this.clearCache();
    // The next MQTT update will repopulate with correct entities
  }
  
  /**
   * Register cached climate devices with the device registry
   * This ensures climate widgets show all devices immediately
   */
  private registerCachedDevices(): void {
    // Extract climate device IDs from cache
    const climateDeviceIds = Object.keys(this.cachedDeviceStates)
      .filter(id => id.startsWith('climate.'))
      .map(id => id.replace('climate.', '')); // Remove prefix to get device ID
    
    if (climateDeviceIds.length > 0) {
      this.deviceRegistry.registerDevices('climate', climateDeviceIds);
    }
  }
} 